/**
 * `telegram/startApp.ts` — the Mini App's `startapp` keys → member routes
 * (#2074).
 *
 * A card's button in Telegram opens the Mini App with a key
 * (`?startapp=changes`, or `start_param` on a `t.me/<bot>/app` link); the
 * backend builds the key from the same route (`channels/telegram/copy.py`
 * `MINIAPP_START_KEYS`). Unknown or malformed keys open `/me`, the app's
 * opening screen (#2945: Team today for a member of a roster team, else the
 * Calendar). `cal` keeps the Calendar (`stateForStartParam`).
 */

import { KEEP_CALENDAR_STATE } from "../memberapp/openingScreen";

const FIXED: Record<string, string> = {
  cal: "/me",
  inbox: "/me/inbox",
  tasks: "/me/tasks",
  changes: "/me/changes",
  // #2075 — the chat's own buttons (/swaps, /requests, /sick).
  swaps: "/me/swaps",
  sick: "/me/requests/new?type=mc",
  block: "/me/requests/new?type=no_oncall",
  // #2071 — the Team tab: a lead's Team home; #2945 — a member's Team today.
  team: "/me/team",
  // #2071 — the lead's People on the phone (lead phone app B6).
  people: "/me/team/people",
  // #2071 B3 — the lead's Review (leave requests + held swaps).
  review: "/me/team/review",
  // #2193 — the member's Fixed shifts (her answer card's button).
  fixed: "/me/fixed-shifts",
};

const ID = /^[A-Za-z0-9-]{1,64}$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * #2945 — the location state a start key's route carries. `cal` asks for the
 * Calendar by name, so it keeps the Calendar even though a member's app
 * otherwise opens on Team today (`memberapp/openingScreen.ts`).
 */
export function stateForStartParam(key: string | null | undefined): unknown {
  return key === "cal" ? KEEP_CALENDAR_STATE : undefined;
}

export function routeForStartParam(key: string | null | undefined): string {
  if (!key) return "/me";
  const fixed = FIXED[key];
  if (fixed) return fixed;
  const split = key.indexOf("_");
  if (split < 0) return "/me";
  const kind = key.slice(0, split);
  const value = key.slice(split + 1);
  if (kind === "req" && ID.test(value)) return `/me/requests/${value}`;
  if (kind === "swap" && ID.test(value)) return `/me/swaps?focus=${value}`;
  // #2666 — the lead's cover card, its Change sheet open.
  if (kind === "covchg" && ID.test(value)) return `/me/inbox?cover=${value}&change=1`;
  if (kind === "map" && ID.test(value)) return `/teams/${value}/map`; // #2631 Map member tasks
  if (kind === "day" && DAY.test(value)) return `/me?date=${value}`;
  if (kind === "whoson" && DAY.test(value)) return `/me/whos-on?date=${value}`;
  // #2071 B2 — the lead's day with its call-back ladder open (the off-sick
  // alert's button can point here; a member is sent on to the Calendar).
  if (kind === "ladder" && DAY.test(value)) return `/me/team/roster?date=${value}&ladder=1`;
  // #2268 — the lead's Roster by day on a date, its day check above the
  // seats (`/whoson`'s button while the chat's day check line is missing).
  if (kind === "roster" && DAY.test(value)) return `/me/team/roster?date=${value}`;
  return "/me";
}
